import {
  AmendCaptureInput,
  GetCaptureResponse,
  GetGroupResponse,
  ListCapturesQuery,
  ListCapturesResponse,
  ListGroupsQuery,
  ListGroupsResponse,
  StoredCapture,
  StoredConfig,
  StoredGroup,
  StoredInternal,
  Page,
  UpdateCaptureInput,
  UpdateConfigInput,
  UpdateGroupInput,
} from "../../../types";

const API_URL = import.meta.env.VITE_API_URL;
const AUTH_URL = import.meta.env.VITE_AUTH_URL;

export const getToken = () => window?.localStorage?.getItem?.("auth") ?? undefined;
export const setToken = (token: string) => window.localStorage.setItem("auth", token);
export const clearToken = () => window.localStorage.removeItem("auth");

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const authFetch = async <T>(path: string, props?: RequestInit): Promise<T> => {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...props,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { authorization: token } : {}),
      ...props?.headers,
    },
  });
  const body = await res.json().catch(() => undefined);
  if (!res.ok || !body?.success) {
    throw new ApiError(body?.statusCode ?? res.status, body?.error || `Request failed (${res.status})`);
  }
  return body.data as T;
};

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    search.set(k, Array.isArray(v) ? v.join(",") : String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

// doridium's auth is a separate origin, hit directly — its /auth/* routes
// are CORS-open (same arrangement as todoit).
export const AuthApi = {
  login: async (input: { username: string; password: string }) => {
    const res = await fetch(`${AUTH_URL}/auth/login`, { method: "POST", body: JSON.stringify(input) });
    return res.json();
  },
  getUser: async (token: string) => {
    const res = await fetch(`${AUTH_URL}/auth/user`, { method: "GET", headers: { authorization: token } });
    return res.json();
  },
};

export const CaptureApi = {
  list: (query: ListCapturesQuery) => authFetch<ListCapturesResponse>(`/api/capture${qs(query)}`),
  get: (id: string) => authFetch<GetCaptureResponse>(`/api/capture/${encodeURIComponent(id)}`),
  update: (input: UpdateCaptureInput) =>
    authFetch<StoredCapture>(`/api/capture/${encodeURIComponent(input.id)}`, {
      method: "PATCH",
      body: JSON.stringify({ ...input, type: "capture" }),
    }),
  amend: (input: AmendCaptureInput) =>
    authFetch<GetCaptureResponse>(`/api/capture/${encodeURIComponent(input.id)}/amend`, {
      method: "POST",
      body: JSON.stringify({ breadcrumbs: input.breadcrumbs }),
    }),
  delete: (id: string) =>
    authFetch<{ id: string; deleted: number }>(`/api/capture/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export const ConfigApi = {
  get: () => authFetch<StoredConfig>(`/api/config`),
  update: (input: UpdateConfigInput) =>
    authFetch<StoredConfig>(`/api/config/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify({ ...input, type: "config" }),
    }),
};

export const InternalApi = {
  list: (cursor?: string) => authFetch<Page<StoredInternal>>(`/api/internal${qs({ cursor, limit: 50 })}`),
  delete: (id: string) => authFetch<{ id: string }>(`/api/internal/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export const GroupApi = {
  list: (query: ListGroupsQuery) => authFetch<ListGroupsResponse>(`/api/group${qs(query)}`),
  get: (id: string, cursor?: string) => authFetch<GetGroupResponse>(`/api/group/${encodeURIComponent(id)}${qs({ cursor })}`),
  update: (input: UpdateGroupInput) =>
    authFetch<StoredGroup>(`/api/group/${encodeURIComponent(input.id)}`, {
      method: "PATCH",
      body: JSON.stringify({ ...input, type: "group" }),
    }),
  delete: (id: string) => authFetch<{ id: string }>(`/api/group/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
